import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, MapPin, Vote, CheckCircle } from 'lucide-react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

// City coordinates database with accurate lat/lng
const cityCoords = {
  // USA Cities
  'Los Angeles': { lat: 34.0522, lng: -118.2437, landmark: 'Hollywood Sign' },
  'San Francisco': { lat: 37.7749, lng: -122.4194, landmark: 'Golden Gate Bridge' },
  'San Diego': { lat: 32.7157, lng: -117.1611, landmark: 'San Diego Zoo' },
  'Sacramento': { lat: 38.5816, lng: -121.4944, landmark: 'State Capitol' },
  'New York City': { lat: 40.7128, lng: -74.0060, landmark: 'Statue of Liberty' },
  'Buffalo': { lat: 42.8864, lng: -78.8784, landmark: 'Niagara Falls' },
  'Albany': { lat: 42.6526, lng: -73.7562, landmark: 'Empire State Plaza' },
  'Houston': { lat: 29.7604, lng: -95.3698, landmark: 'Space Center Houston' },
  'Austin': { lat: 30.2672, lng: -97.7431, landmark: 'Texas State Capitol' },
  'Dallas': { lat: 32.7767, lng: -96.7970, landmark: 'Reunion Tower' },
  'San Antonio': { lat: 29.4241, lng: -98.4936, landmark: 'The Alamo' },
  'Miami': { lat: 25.7617, lng: -80.1918, landmark: 'Art Deco District' },
  'Orlando': { lat: 28.5383, lng: -81.3792, landmark: 'Walt Disney World' },
  'Tampa': { lat: 27.9506, lng: -82.4572, landmark: 'Busch Gardens' },
  'Chicago': { lat: 41.8781, lng: -87.6298, landmark: 'Willis Tower' },
  'Springfield': { lat: 39.7817, lng: -89.6501, landmark: 'Lincoln Home' },
  'Atlanta': { lat: 33.7490, lng: -84.3880, landmark: 'Georgia Aquarium' },
  'Savannah': { lat: 32.0809, lng: -81.0912, landmark: 'Forsyth Park' },
  'New Orleans': { lat: 29.9511, lng: -90.0715, landmark: 'French Quarter' },
  'Baton Rouge': { lat: 30.4515, lng: -91.1871, landmark: 'State Capitol' },
  'Las Vegas': { lat: 36.1699, lng: -115.1398, landmark: 'Las Vegas Strip' },
  'Reno': { lat: 39.5296, lng: -119.8138, landmark: 'The Arch' },
  'Seattle': { lat: 47.6062, lng: -122.3321, landmark: 'Space Needle' },
  'Spokane': { lat: 47.6588, lng: -117.4260, landmark: 'Riverfront Park' },
  'Denver': { lat: 39.7392, lng: -104.9903, landmark: 'Red Rocks' },
  'Colorado Springs': { lat: 38.8339, lng: -104.8214, landmark: 'Garden of Gods' },
  'Phoenix': { lat: 33.4484, lng: -112.0740, landmark: 'Camelback Mountain' },
  'Tucson': { lat: 32.2226, lng: -110.9747, landmark: 'Saguaro National Park' },
  'Nashville': { lat: 36.1627, lng: -86.7816, landmark: 'Grand Ole Opry' },
  'Memphis': { lat: 35.1495, lng: -90.0490, landmark: 'Graceland' },
  'Philadelphia': { lat: 39.9526, lng: -75.1652, landmark: 'Liberty Bell' },
  'Pittsburgh': { lat: 40.4406, lng: -79.9959, landmark: 'Point State Park' },
  'Columbus': { lat: 39.9612, lng: -82.9988, landmark: 'Ohio Stadium' },
  'Cleveland': { lat: 41.4993, lng: -81.6944, landmark: 'Rock Hall of Fame' },
  'Cincinnati': { lat: 39.1031, lng: -84.5120, landmark: 'Great American Ballpark' },
  'Detroit': { lat: 42.3314, lng: -83.0458, landmark: 'Renaissance Center' },
  'Grand Rapids': { lat: 42.9634, lng: -85.6681, landmark: 'Frederik Meijer Gardens' },
  // Canada Cities
  'Toronto': { lat: 43.6532, lng: -79.3832, landmark: 'CN Tower' },
  'Ottawa': { lat: 45.4215, lng: -75.6972, landmark: 'Parliament Hill' },
  'Hamilton': { lat: 43.2557, lng: -79.8711, landmark: 'Dundurn Castle' },
  'Montreal': { lat: 45.5017, lng: -73.5673, landmark: 'Notre-Dame Basilica' },
  'Quebec City': { lat: 46.8139, lng: -71.2080, landmark: 'Château Frontenac' },
  'Vancouver': { lat: 49.2827, lng: -123.1207, landmark: 'Stanley Park' },
  'Victoria': { lat: 48.4284, lng: -123.3656, landmark: 'Inner Harbour' },
  'Calgary': { lat: 51.0447, lng: -114.0719, landmark: 'Calgary Tower' },
  'Edmonton': { lat: 53.5461, lng: -113.4938, landmark: 'West Edmonton Mall' },
  'Winnipeg': { lat: 49.8951, lng: -97.1384, landmark: 'The Forks' },
  'Saskatoon': { lat: 52.1332, lng: -106.6700, landmark: 'Meewasin Trail' },
  'Regina': { lat: 50.4452, lng: -104.6189, landmark: 'Wascana Centre' },
  // South Korea Cities
  'Seoul': { lat: 37.5665, lng: 126.9780, landmark: 'Gyeongbokgung Palace' },
  'Busan': { lat: 35.1796, lng: 129.0756, landmark: 'Haedong Yonggungsa' },
  'Incheon': { lat: 37.4563, lng: 126.7052, landmark: 'Songdo Central Park' },
  'Daegu': { lat: 35.8714, lng: 128.6014, landmark: 'Apsan Park' },
  'Daejeon': { lat: 36.3504, lng: 127.3845, landmark: 'Expo Science Park' },
  'Gwangju': { lat: 35.1595, lng: 126.8526, landmark: 'May 18th Memorial' },
  'Ulsan': { lat: 35.5384, lng: 129.3114, landmark: 'Ulsan Grand Park' },
  'Suwon': { lat: 37.2636, lng: 127.0286, landmark: 'Hwaseong Fortress' },
  'Gimpo': { lat: 37.6152, lng: 126.7156, landmark: 'Gimpo Hangang Park' },
};

// State center coordinates for accurate zoom
const stateCoords = {
  'California': { lat: 36.7783, lng: -119.4179 },
  'New York': { lat: 42.1657, lng: -74.9481 },
  'Texas': { lat: 31.9686, lng: -99.9018 },
  'Florida': { lat: 27.6648, lng: -81.5158 },
  'Illinois': { lat: 40.6331, lng: -89.3985 },
  'Georgia': { lat: 32.1656, lng: -82.9001 },
  'Louisiana': { lat: 30.9843, lng: -91.9623 },
  'Nevada': { lat: 38.8026, lng: -116.4194 },
  'Washington': { lat: 47.7511, lng: -120.7401 },
  'Colorado': { lat: 39.5501, lng: -105.7821 },
  'Arizona': { lat: 34.0489, lng: -111.0937 },
  'Tennessee': { lat: 35.5175, lng: -86.5804 },
  'Pennsylvania': { lat: 41.2033, lng: -77.1945 },
  'Ohio': { lat: 40.4173, lng: -82.9071 },
  'Michigan': { lat: 44.3148, lng: -85.6024 },
  // Canada provinces
  'Ontario': { lat: 51.2538, lng: -85.3232 },
  'Quebec': { lat: 52.9399, lng: -73.5491 },
  'British Columbia': { lat: 53.7267, lng: -127.6476 },
  'Alberta': { lat: 53.9333, lng: -116.5765 },
  'Manitoba': { lat: 53.7609, lng: -98.8139 },
  'Saskatchewan': { lat: 52.9399, lng: -106.4509 },
};

// Country/State data
const countryData = {
  usa: {
    name: 'USA',
    lat: 39.8283,
    lng: -98.5795,
    zoom: 1.8,
    color: '#22c55e',
    states: {
      'California': { cities: ['Los Angeles', 'San Francisco', 'San Diego', 'Sacramento'] },
      'New York': { cities: ['New York City', 'Buffalo', 'Albany'] },
      'Texas': { cities: ['Houston', 'Austin', 'Dallas', 'San Antonio'] },
      'Florida': { cities: ['Miami', 'Orlando', 'Tampa'] },
      'Illinois': { cities: ['Chicago', 'Springfield'] },
      'Georgia': { cities: ['Atlanta', 'Savannah'] },
      'Louisiana': { cities: ['New Orleans', 'Baton Rouge'] },
      'Nevada': { cities: ['Las Vegas', 'Reno'] },
      'Washington': { cities: ['Seattle', 'Spokane'] },
      'Colorado': { cities: ['Denver', 'Colorado Springs'] },
      'Arizona': { cities: ['Phoenix', 'Tucson'] },
      'Tennessee': { cities: ['Nashville', 'Memphis'] },
      'Pennsylvania': { cities: ['Philadelphia', 'Pittsburgh'] },
      'Ohio': { cities: ['Columbus', 'Cleveland', 'Cincinnati'] },
      'Michigan': { cities: ['Detroit', 'Grand Rapids'] },
    }
  },
  korea: {
    name: 'South Korea',
    lat: 36.5,
    lng: 127.5,
    zoom: 1.45,
    color: '#eab308',
    cities: ['Seoul', 'Busan', 'Incheon', 'Daegu', 'Daejeon', 'Gwangju', 'Ulsan', 'Suwon', 'Gimpo'],
    votesRequired: 250
  },
  canada: {
    name: 'Canada',
    lat: 56.1304,
    lng: -106.3468,
    zoom: 1.7,
    color: '#eab308',
    provinces: {
      'Ontario': { cities: ['Toronto', 'Ottawa', 'Hamilton'] },
      'Quebec': { cities: ['Montreal', 'Quebec City'] },
      'British Columbia': { cities: ['Vancouver', 'Victoria'] },
      'Alberta': { cities: ['Calgary', 'Edmonton'] },
      'Manitoba': { cities: ['Winnipeg'] },
      'Saskatchewan': { cities: ['Saskatoon', 'Regina'] },
    },
    votesRequired: 200
  }
};

const Globe = () => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const rendererRef = useRef(null);
  const markersRef = useRef([]);
  const animationRef = useRef(null);
  
  const [currentLevel, setCurrentLevel] = useState('globe');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [showVoteForm, setShowVoteForm] = useState(false);
  const [voterName, setVoterName] = useState('');
  const [liveVotes, setLiveVotes] = useState({});
  const [userVotesThisMonth, setUserVotesThisMonth] = useState(0);
  const [isZooming, setIsZooming] = useState(false);
  const [showLockedMessage, setShowLockedMessage] = useState(null);
  const [voteSuccess, setVoteSuccess] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Load votes from API
  useEffect(() => {
    const loadVotes = async () => {
      try {
        const res = await fetch(API_URL + '/api/votes');
        if (res.ok) {
          const data = await res.json();
          setLiveVotes(data.votes || {});
        }
      } catch (e) { /* ignore */ }
    };
    loadVotes();
    const stored = localStorage.getItem('rhyVotes');
    if (stored) {
      const p = JSON.parse(stored);
      if (p.month === new Date().getMonth()) setUserVotesThisMonth(p.count);
    }
  }, [API_URL]);

  // Convert lat/lng to 3D position
  const latLngToPosition = useCallback((lat, lng, radius = 1.02) => {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lng + 180) * (Math.PI / 180);
    return {
      x: radius * Math.sin(phi) * Math.cos(theta),
      y: radius * Math.cos(phi),
      z: radius * Math.sin(phi) * Math.sin(theta)
    };
  }, []);

  // Smooth camera zoom to exact location
  const zoomToLocation = useCallback((lat, lng, distance, duration = 1200) => {
    if (!cameraRef.current || !controlsRef.current) return;
    
    setIsZooming(true);
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    controls.autoRotate = false;
    
    const startPos = { x: camera.position.x, y: camera.position.y, z: camera.position.z };
    const targetPos = latLngToPosition(lat, lng, distance);
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      
      camera.position.x = startPos.x + (targetPos.x - startPos.x) * eased;
      camera.position.y = startPos.y + (targetPos.y - startPos.y) * eased;
      camera.position.z = startPos.z + (targetPos.z - startPos.z) * eased;
      
      camera.lookAt(0, 0, 0);
      controls.update();
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsZooming(false);
      }
    };
    animate();
  }, [latLngToPosition]);

  // Clear markers
  const clearMarkers = useCallback(() => {
    if (!sceneRef.current) return;
    markersRef.current.forEach(marker => {
      sceneRef.current.remove(marker);
      if (marker.geometry) marker.geometry.dispose();
      if (marker.material) marker.material.dispose();
    });
    markersRef.current = [];
  }, []);

  // Add glowing marker
  const addMarker = useCallback((lat, lng, color, size = 0.03, pulseSpeed = 0) => {
    if (!sceneRef.current) return;
    
    const pos = latLngToPosition(lat, lng, 1.02);
    
    const geo = new THREE.SphereGeometry(size, 16, 16);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 });
    const marker = new THREE.Mesh(geo, mat);
    marker.position.set(pos.x, pos.y, pos.z);
    marker.userData = { pulseSpeed, baseSize: size };
    sceneRef.current.add(marker);
    markersRef.current.push(marker);
    
    if (pulseSpeed > 0) {
      const ringGeo = new THREE.SphereGeometry(size * 2, 16, 16);
      const ringMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.3 });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.set(pos.x, pos.y, pos.z);
      ring.userData = { pulseSpeed, baseSize: size * 2, isPulse: true };
      sceneRef.current.add(ring);
      markersRef.current.push(ring);
    }
    
    return marker;
  }, [latLngToPosition]);

  // Initialize Three.js
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.z = 3;
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const loader = new THREE.TextureLoader();

    // Earth
    const earthGeo = new THREE.SphereGeometry(1, 128, 128);
    const earthMat = new THREE.MeshPhongMaterial({
      map: loader.load('https://unpkg.com/three-globe@2.31.0/example/img/earth-blue-marble.jpg'),
      bumpMap: loader.load('https://unpkg.com/three-globe@2.31.0/example/img/earth-topology.png'),
      bumpScale: 0.02,
      specularMap: loader.load('https://unpkg.com/three-globe@2.31.0/example/img/earth-water.png'),
      specular: new THREE.Color(0x222222),
      shininess: 10,
    });
    scene.add(new THREE.Mesh(earthGeo, earthMat));

    // Borders
    const bordersGeo = new THREE.SphereGeometry(1.003, 128, 128);
    const bordersMat = new THREE.MeshBasicMaterial({
      map: loader.load('https://unpkg.com/three-globe@2.31.0/example/img/earth-dark.jpg'),
      transparent: true,
      opacity: 0.25,
      blending: THREE.AdditiveBlending,
    });
    scene.add(new THREE.Mesh(bordersGeo, bordersMat));

    // Clouds
    const cloudsGeo = new THREE.SphereGeometry(1.015, 64, 64);
    const cloudsMat = new THREE.MeshPhongMaterial({
      map: loader.load('https://unpkg.com/three-globe@2.31.0/example/img/earth-clouds.png'),
      transparent: true,
      opacity: 0.2,
    });
    const clouds = new THREE.Mesh(cloudsGeo, cloudsMat);
    scene.add(clouds);

    // Atmosphere
    const atmosGeo = new THREE.SphereGeometry(1.12, 64, 64);
    const atmosMat = new THREE.ShaderMaterial({
      vertexShader: `varying vec3 vNormal; void main() { vNormal = normalize(normalMatrix * normal); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: `varying vec3 vNormal; void main() { float intensity = pow(0.6 - dot(vNormal, vec3(0, 0, 1)), 2.0); gl_FragColor = vec4(0.3, 0.6, 1.0, 1.0) * intensity; }`,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
      transparent: true,
    });
    scene.add(new THREE.Mesh(atmosGeo, atmosMat));

    // Stars
    const starGeo = new THREE.BufferGeometry();
    const starPositions = new Float32Array(6000 * 3);
    for (let i = 0; i < 6000 * 3; i += 3) {
      starPositions[i] = (Math.random() - 0.5) * 100;
      starPositions[i + 1] = (Math.random() - 0.5) * 100;
      starPositions[i + 2] = (Math.random() - 0.5) * 100;
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    const stars = new THREE.Points(starGeo, new THREE.PointsMaterial({ size: 0.05, color: 0xffffff, transparent: true, opacity: 0.7 }));
    scene.add(stars);

    // Lighting
    scene.add(new THREE.DirectionalLight(0xffffff, 1.5).translateX(5).translateY(3).translateZ(5));
    scene.add(new THREE.AmbientLight(0x404040, 0.8));
    scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 0.4));

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.rotateSpeed = 0.8;
    controls.zoomSpeed = 1.0;
    controls.minDistance = 1.15;
    controls.maxDistance = 5;
    controls.enablePan = false;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.3;
    controlsRef.current = controls;

    // Animation
    let time = 0;
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      time += 0.02;
      
      clouds.rotation.y += 0.0002;
      stars.rotation.y += 0.0001;
      
      markersRef.current.forEach(marker => {
        if (marker.userData.pulseSpeed > 0) {
          const scale = 1 + Math.sin(time * marker.userData.pulseSpeed) * 0.3;
          marker.scale.set(scale, scale, scale);
          if (marker.userData.isPulse) {
            marker.material.opacity = 0.2 + Math.sin(time * marker.userData.pulseSpeed) * 0.15;
          }
        }
      });
      
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
      container.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);

  // Update markers based on level
  useEffect(() => {
    clearMarkers();
    
    if (currentLevel === 'globe') {
      addMarker(countryData.usa.lat, countryData.usa.lng, countryData.usa.color, 0.08, 3);
      addMarker(countryData.canada.lat, countryData.canada.lng, countryData.canada.color, 0.06, 3);
      addMarker(countryData.korea.lat, countryData.korea.lng, countryData.korea.color, 0.04, 3);
    } else if (currentLevel === 'country' && selectedCountry) {
      if (selectedCountry === 'usa') {
        Object.keys(countryData.usa.states).forEach(stateName => {
          const coords = stateCoords[stateName];
          if (coords) addMarker(coords.lat, coords.lng, '#22c55e', 0.02, 2);
        });
      } else if (selectedCountry === 'canada') {
        Object.keys(countryData.canada.provinces).forEach(provinceName => {
          const coords = stateCoords[provinceName];
          if (coords) addMarker(coords.lat, coords.lng, '#eab308', 0.02, 2);
        });
      } else if (selectedCountry === 'korea') {
        countryData.korea.cities.forEach(cityName => {
          const city = cityCoords[cityName];
          if (city) addMarker(city.lat, city.lng, '#eab308', 0.012, 2);
        });
      }
    } else if (currentLevel === 'state' && selectedState) {
      let cities = [];
      if (selectedCountry === 'usa') {
        cities = countryData.usa.states[selectedState]?.cities || [];
      } else if (selectedCountry === 'canada') {
        cities = countryData.canada.provinces[selectedState]?.cities || [];
      }
      cities.forEach(cityName => {
        const city = cityCoords[cityName];
        if (city) addMarker(city.lat, city.lng, '#f472b6', 0.015, 2);
      });
    } else if (currentLevel === 'city' && selectedCity) {
      const city = cityCoords[selectedCity];
      if (city) addMarker(city.lat, city.lng, '#ef4444', 0.02, 4);
    }
  }, [currentLevel, selectedCountry, selectedState, selectedCity, addMarker, clearMarkers]);

  // Handlers
  const handleCountryClick = (countryKey) => {
    const country = countryData[countryKey];
    setSelectedCountry(countryKey);
    setCurrentLevel('country');
    zoomToLocation(country.lat, country.lng, country.zoom);
  };

  const handleStateClick = (stateName) => {
    setSelectedState(stateName);
    const coords = stateCoords[stateName];
    if (coords) {
      setCurrentLevel('state');
      zoomToLocation(coords.lat, coords.lng, 1.4);
    }
  };

  const handleCityClick = (cityName) => {
    const city = cityCoords[cityName];
    if (city) {
      setSelectedCity(cityName);
      setCurrentLevel('city');
      zoomToLocation(city.lat, city.lng, 1.2);
    }
  };

  const goBack = () => {
    setShowVoteForm(false);
    setShowLockedMessage(null);
    setVoteSuccess(false);
    
    if (currentLevel === 'city') {
      setSelectedCity(null);
      if (selectedCountry === 'korea') {
        setCurrentLevel('country');
        zoomToLocation(countryData.korea.lat, countryData.korea.lng, countryData.korea.zoom);
      } else {
        setCurrentLevel('state');
        const coords = stateCoords[selectedState];
        if (coords) zoomToLocation(coords.lat, coords.lng, 1.4);
      }
    } else if (currentLevel === 'state') {
      setSelectedState(null);
      setCurrentLevel('country');
      const country = countryData[selectedCountry];
      zoomToLocation(country.lat, country.lng, country.zoom);
    } else if (currentLevel === 'country') {
      setSelectedCountry(null);
      setCurrentLevel('globe');
      zoomToLocation(20, 0, 3);
      if (controlsRef.current) controlsRef.current.autoRotate = true;
    }
  };

  const handleVote = async () => {
    if (userVotesThisMonth >= 3 || !voterName.trim() || !selectedCity) return;
    try {
      await fetch(API_URL + '/api/votes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: selectedCity, name: voterName })
      });
      
      // Update vote count immediately
      setLiveVotes(prev => ({ ...prev, [selectedCity]: (prev[selectedCity] || 0) + 1 }));
      const newCount = userVotesThisMonth + 1;
      setUserVotesThisMonth(newCount);
      localStorage.setItem('rhyVotes', JSON.stringify({ count: newCount, month: new Date().getMonth() }));
      setShowVoteForm(false);
      setVoterName('');
      setVoteSuccess(true);
      
      // Hide success after 3 seconds
      setTimeout(() => setVoteSuccess(false), 3000);
    } catch (e) { /* ignore */ }
  };

  const getLocationItems = () => {
    if (currentLevel === 'country') {
      if (selectedCountry === 'usa') {
        return { type: 'states', items: Object.keys(countryData.usa.states) };
      } else if (selectedCountry === 'canada') {
        return { type: 'provinces', items: Object.keys(countryData.canada.provinces) };
      } else if (selectedCountry === 'korea') {
        return { type: 'cities', items: countryData.korea.cities };
      }
    } else if (currentLevel === 'state') {
      if (selectedCountry === 'usa') {
        return { type: 'cities', items: countryData.usa.states[selectedState]?.cities || [] };
      } else if (selectedCountry === 'canada') {
        return { type: 'cities', items: countryData.canada.provinces[selectedState]?.cities || [] };
      }
    }
    return { type: null, items: [] };
  };

  const { type: itemType, items } = getLocationItems();
  const isLocked = selectedCountry && selectedCountry !== 'usa' && countryData[selectedCountry]?.votesRequired;

  return (
    <section className="relative py-16 px-4" data-testid="globe-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-2">VOTE FOR RHY TO COME TO YOUR CITY</h2>
          <p className="font-mono text-xs text-white/50">
            {currentLevel === 'globe' && 'Spin the globe • Tap a glowing country'}
            {currentLevel === 'country' && `Exploring ${countryData[selectedCountry]?.name}`}
            {currentLevel === 'state' && `${selectedState} • Select a city`}
            {currentLevel === 'city' && `${selectedCity} • Vote now!`}
          </p>
        </div>
        
        <div className="relative bg-black/80 border border-white/10 rounded-3xl overflow-hidden">
          <div ref={containerRef} className="w-full h-[550px]" />
          
          {/* Back Button */}
          {currentLevel !== 'globe' && (
            <button 
              onClick={goBack}
              disabled={isZooming}
              className="absolute top-4 left-4 z-50 flex items-center gap-2 px-4 py-2 bg-black/70 hover:bg-white/20 border border-white/20 rounded-lg text-white text-sm backdrop-blur-sm transition-all disabled:opacity-50"
            >
              <ArrowLeft className="w-4 h-4" /> BACK
            </button>
          )}

          {/* Globe Level - Country Buttons */}
          {currentLevel === 'globe' && !isZooming && (
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-1/2 left-1/4 -translate-y-1/2 -translate-x-1/2 pointer-events-auto">
                <button onClick={() => handleCountryClick('usa')} className="px-5 py-3 bg-green-500/90 hover:bg-green-400 border-2 border-green-300 rounded-xl text-white font-bold shadow-lg shadow-green-500/40 transition-all hover:scale-110 animate-pulse">
                  🇺🇸 USA
                </button>
              </div>
              <div className="absolute top-1/3 left-1/3 -translate-y-1/2 pointer-events-auto">
                <button onClick={() => handleCountryClick('canada')} className="px-4 py-2 bg-yellow-500/90 hover:bg-yellow-400 border-2 border-yellow-300 rounded-xl text-white font-bold shadow-lg shadow-yellow-500/40 transition-all hover:scale-110 animate-pulse">
                  🇨🇦 CANADA
                </button>
              </div>
              <div className="absolute top-1/2 right-1/4 -translate-y-1/2 translate-x-1/2 pointer-events-auto">
                <button onClick={() => handleCountryClick('korea')} className="px-4 py-2 bg-yellow-500/90 hover:bg-yellow-400 border-2 border-yellow-300 rounded-xl text-white font-bold shadow-lg shadow-yellow-500/40 transition-all hover:scale-110 animate-pulse">
                  🇰🇷 SOUTH KOREA
                </button>
              </div>
              <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm font-mono bg-black/70 px-6 py-2 rounded-full">
                🌍 DRAG TO SPIN • TAP GLOWING COUNTRY
              </p>
            </div>
          )}

          {/* Country/State Level - Location List */}
          {(currentLevel === 'country' || currentLevel === 'state') && !isZooming && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/90 to-transparent p-4 max-h-[250px] overflow-y-auto">
              <div className="max-w-3xl mx-auto">
                <h3 className="text-center font-heading text-lg text-white mb-3">
                  {currentLevel === 'country' && (
                    <>
                      {selectedCountry === 'usa' && '🇺🇸 SELECT A STATE'}
                      {selectedCountry === 'canada' && '🇨🇦 SELECT A PROVINCE'}
                      {selectedCountry === 'korea' && '🇰🇷 SELECT A CITY'}
                    </>
                  )}
                  {currentLevel === 'state' && `📍 ${selectedState?.toUpperCase()}`}
                </h3>
                
                {isLocked && currentLevel === 'country' && (
                  <p className="text-center text-red-400 text-sm mb-3">🔒 Voting unlocks at {countryData[selectedCountry].votesRequired} total votes</p>
                )}
                
                <div className="flex flex-wrap gap-2 justify-center">
                  {items.map(item => (
                    <button
                      key={item}
                      onClick={() => {
                        if (itemType === 'cities') {
                          if (isLocked) setShowLockedMessage(countryData[selectedCountry].votesRequired);
                          else handleCityClick(item);
                        } else handleStateClick(item);
                      }}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all hover:scale-105 ${
                        selectedCountry === 'usa' 
                          ? 'bg-green-900/60 border border-green-500/40 text-green-300 hover:bg-green-500/40'
                          : 'bg-yellow-900/60 border border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/40'
                      }`}
                    >
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {item}
                      {itemType === 'cities' && liveVotes[item] > 0 && (
                        <span className="ml-2 text-xs text-red-400 font-bold">({liveVotes[item]})</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* City Level - Vote UI */}
          {currentLevel === 'city' && !isZooming && selectedCity && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/95 to-transparent p-6">
              <div className="max-w-md mx-auto text-center">
                <h3 className="font-heading text-3xl text-white mb-1">{selectedCity}</h3>
                <p className="text-white/60 text-sm">{cityCoords[selectedCity]?.landmark && `🏛️ ${cityCoords[selectedCity].landmark}`}</p>
                <p className="text-white/40 text-xs mt-1">{selectedState || countryData[selectedCountry]?.name}</p>
                
                {/* Live Vote Count - Always visible */}
                <div className="flex items-center justify-center gap-3 my-4 px-6 py-3 bg-black/60 rounded-full border border-white/20 inline-flex mx-auto">
                  <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-red-400 font-bold text-sm">LIVE VOTES</span>
                  <span className="text-white text-3xl font-bold">{liveVotes[selectedCity] || 0}</span>
                </div>
                
                {/* Vote Success Message */}
                {voteSuccess && (
                  <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 rounded-xl animate-pulse">
                    <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-bold">VOTE RECORDED!</p>
                    <p className="text-white text-2xl font-bold">{liveVotes[selectedCity]} total votes</p>
                  </div>
                )}
                
                {!showVoteForm && !voteSuccess ? (
                  <button
                    onClick={() => setShowVoteForm(true)}
                    disabled={userVotesThisMonth >= 3 || isLocked}
                    className={`w-full max-w-xs mx-auto flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-lg text-white transition-all ${
                      userVotesThisMonth >= 3 
                        ? 'bg-gray-600 cursor-not-allowed' 
                        : isLocked
                          ? 'bg-gray-600 cursor-not-allowed'
                          : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:scale-105 shadow-lg shadow-green-500/30'
                    }`}
                  >
                    <Vote className="w-5 h-5" />
                    {userVotesThisMonth >= 3 
                      ? 'NO VOTES LEFT THIS MONTH' 
                      : isLocked 
                        ? `LOCKED`
                        : 'VOTE FOR RHY TO COME HERE'}
                  </button>
                ) : showVoteForm && (
                  <div className="bg-black/60 p-6 rounded-2xl border border-white/20 backdrop-blur-sm">
                    <h4 className="text-white font-heading text-xl mb-4">Cast Your Vote</h4>
                    <input
                      type="text"
                      placeholder="Enter your name"
                      value={voterName}
                      onChange={e => setVoterName(e.target.value)}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white mb-4 focus:outline-none focus:border-green-500"
                    />
                    <div className="flex gap-3">
                      <button onClick={handleVote} className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl">✓ SUBMIT</button>
                      <button onClick={() => setShowVoteForm(false)} className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl">✕</button>
                    </div>
                    <p className="text-white/40 text-xs mt-3">{3 - userVotesThisMonth} votes remaining</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Locked Message */}
          {showLockedMessage && (
            <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowLockedMessage(null)}>
              <div className="bg-gradient-to-br from-red-900 to-red-950 border-2 border-red-500/50 rounded-2xl p-10 text-center">
                <p className="text-white text-2xl font-heading mb-3">🔒 UNLOCK WITH</p>
                <p className="text-red-400 text-6xl font-bold">{showLockedMessage}</p>
                <p className="text-red-300 text-xl mt-2">TOTAL VOTES</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Globe;
